import { UndirectedGraph } from "data-structure-typed";
import { Point } from "@archipad-js/core/utils";
import { PlanMetadata, Space } from "./plan-metadata";

/**
 * Default color palette for plan spaces.
 *
 * Colors are assigned to adjacent spaces in such a way that directly adjacent
 * spaces should not share the same palette color whenever possible.
 */
export const PlanSpacePalette = {
    BLUE: "#1976d2",
    GREEN: "#388e3c",
    ORANGE: "#f57c00",
    PURPLE: "#7b1fa2",
    PINK: "#c2185b",
    YELLOW: "#fbc02d",
    GREY: "#455a64",
} as const;

/**
 * Fallback color used when no palette color can be assigned or when a space is
 * missing from the computed color map.
 */
export const PLAN_METADATA_FALLBACK_COLOR = "#000000";

/**
 * Default palette derived from `PlanSpacePalette` values.
 */
const DEFAULT_PALETTE = Object.values(PlanSpacePalette);

/**
 * Number of equally spaced sample rays cast from each polygon edge to detect
 * adjacent spaces.
 */
const RAY_SAMPLES_PER_EDGE = 5;

/**
 * Minimum ray parameter used to ignore intersections at or behind the ray origin.
 */
const MIN_RAY_PARAMETER = 1e-6;

/**
 * Small offset used to shift ray origins slightly outside the source polygon edge
 * to avoid self-intersection with the subject space.
 */
const EPSILON_OFFSET = 1e-6;

/**
 * Threshold used to consider two directions parallel and avoid numerical issues
 * when computing ray/segment intersection.
 */
const PARALLEL_TOLERANCE = 1e-10;

/**
 * Build a color map for plan spaces using the adjacency graph of adjacent
 * polygons.
 *
 * The graph is built by finding adjacent spaces for each polygon and then
 * coloring the resulting undirected graph so adjacent spaces do not share the
 * same palette color.
 *
 * @param planMetadata - Plan metadata containing the spaces to color.
 * @param palette - Optional color palette to assign to spaces.
 * @returns A weak map from space to assigned palette color.
 */
export function getPlanSpaceColorMap(
    planMetadata: PlanMetadata,
    palette: string[] = DEFAULT_PALETTE,
): WeakMap<Space, string> {
    const graph = new UndirectedGraph<Space>();

    const spaces = new Set(planMetadata.spaces);
    for (const space of spaces) {
        graph.addVertex(space.guid, space);
        const neighbors = findAdjacentSpaces(space, spaces);
        for (const neighbor of neighbors) {
            graph.addVertex(neighbor.guid, neighbor);

            if (!graph.hasEdge(space.guid, neighbor.guid) && !graph.hasEdge(neighbor.guid, space.guid)) {
                graph.addEdge(space.guid, neighbor.guid);
            }
        }
    }

    const colorMap = assignGraphColors(graph, palette);
    return colorMap;
}

/**
 * Find all spaces adjacent to a subject space using the plan proximity algorithm.
 *
 * The algorithm casts rays from sample points along each boundary edge of the
 * subject's polygon, in the outward normal direction. The first space hit by
 * each ray is considered adjacent. This allows adjacency detection for complex
 * polygon shapes and for spaces separated by gaps, while avoiding non-nearest
 * spaces behind an intermediate room.
 *
 * @param subject - The reference space whose adjacent neighbors are sought.
 * @param spaces - The full set of spaces to consider for adjacency.
 * @returns A readonly set of spaces that are adjacent to the subject.
 */
export function findAdjacentSpaces(subject: Space, spaces: ReadonlySet<Space>): ReadonlySet<Space> {
    const adjacency = new Set<Space>();
    const otherSpaces = new Set(spaces);
    otherSpaces.delete(subject);

    for (const shapePart of subject.shape) {
        const points = shapePart.points;
        if (points.length < 2) continue;

        const centroid = polygonCentroid(points);

        for (const [a, b] of pairwise(points, true)) {
            const normal = edgeOutwardNormal(a, b, centroid);
            if (normal.x === 0 && normal.y === 0) continue;

            for (let s = 0; s < RAY_SAMPLES_PER_EDGE; s++) {
                const t = (s + 0.5) / RAY_SAMPLES_PER_EDGE;
                const origin = {
                    x: a.x + t * (b.x - a.x) + normal.x * EPSILON_OFFSET,
                    y: a.y + t * (b.y - a.y) + normal.y * EPSILON_OFFSET,
                };

                let minDist = Infinity;
                let nearest: Space | null = null;

                for (const space of otherSpaces) {
                    const dist = rayHitsSpace(origin, normal, space);
                    if (dist !== null && dist < minDist) {
                        minDist = dist;
                        nearest = space;
                    }
                }

                if (nearest !== null) {
                    adjacency.add(nearest);
                }
            }
        }
    }

    return adjacency;
}

function polygonCentroid(points: Point[]): Point {
    let cx = 0;
    let cy = 0;
    for (const p of points) {
        cx += p.x;
        cy += p.y;
    }
    return { x: cx / points.length, y: cy / points.length };
}

/**
 * Returns the outward-facing unit normal for edge a→b, determined by
 * which perpendicular points away from the polygon's centroid.
 */
function edgeOutwardNormal(a: Point, b: Point, centroid: Point): Point {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const n1 = { x: -dy, y: dx };
    const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    const toCentroid = { x: centroid.x - mid.x, y: centroid.y - mid.y };
    const dot = n1.x * toCentroid.x + n1.y * toCentroid.y;
    const n = dot < 0 ? n1 : { x: dy, y: -dx };
    const len = Math.sqrt(n.x * n.x + n.y * n.y);
    if (len === 0) return { x: 0, y: 0 };
    return { x: n.x / len, y: n.y / len };
}

/**
 * Returns the ray parameter t at which the ray (origin + t*dir) intersects
 * the segment [a, b], or null if there is no forward intersection.
 */
function raySegmentIntersection(origin: Point, dir: Point, a: Point, b: Point): number | null {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const denom = dir.x * dy - dir.y * dx;
    if (Math.abs(denom) < PARALLEL_TOLERANCE) return null;
    const t = ((a.x - origin.x) * dy - (a.y - origin.y) * dx) / denom;
    const u = ((a.x - origin.x) * dir.y - (a.y - origin.y) * dir.x) / denom;
    if (t > MIN_RAY_PARAMETER && u >= 0 && u <= 1) return t;
    return null;
}

/**
 * Returns the distance at which a ray first hits the boundary of a space,
 * or null if it misses entirely.
 */
function rayHitsSpace(origin: Point, dir: Point, space: Space): number | null {
    let minT: number | null = null;
    for (const shapePart of space.shape) {
        const pts = shapePart.points;
        for (const [a, b] of pairwise(pts, true)) {
            const t = raySegmentIntersection(origin, dir, a, b);
            if (t !== null && (minT === null || t < minT)) {
                minT = t;
            }
        }
    }
    return minT;
}

function assignGraphColors(graph: UndirectedGraph<Space>, palette: string[]): WeakMap<Space, string> {
    const assignedColors = new WeakMap<Space, string>();
    if (palette.length === 0) {
        return assignedColors;
    }

    const globallyUsed = new Set<string>();
    for (const [key, space] of graph) {
        if (!space) continue;
        const usedByNeighbors = new Set<string>();

        for (const neighbor of graph.getNeighbors(key)) {
            const neighborSpace = neighbor.value;
            if (neighborSpace) {
                const color = assignedColors.get(neighborSpace);
                if (color) {
                    usedByNeighbors.add(color);
                }
            }
        }

        // Prefer a color not used by any neighbor and not yet used globally (for visual diversity).
        // Fall back to any color not used by neighbors, then finally to the first palette entry.
        const color =
            palette.find((c) => !usedByNeighbors.has(c) && !globallyUsed.has(c)) ??
            palette.find((c) => !usedByNeighbors.has(c)) ??
            palette[0];

        assignedColors.set(space, color);
        globallyUsed.add(color);
    }

    return assignedColors;
}

/**
 * Iterate over consecutive element pairs from an iterable.
 *
 * @param iterable - The iterable source.
 * @param wrap - If true, also yield a final tuple linking last -> first.
 * @returns An iterator of `[current, next]` tuples.
 */
function pairwise<T>(iterable: Iterable<T>, wrap = false): IterableIterator<[T, T]> {
    const items = Array.from(iterable);

    return (function* () {
        for (let index = 0; index + 1 < items.length; index++) {
            yield [items[index], items[index + 1]];
        }

        if (wrap && items.length > 0) {
            yield [items[items.length - 1], items[0]];
        }
    })();
}
